import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ArrowLeft, Zap, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/admin");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-navy-deep text-white flex flex-col relative overflow-hidden">
      <div className="absolute inset-0 dots-dark opacity-60 pointer-events-none" />
      <div className="absolute -top-40 left-1/3 h-[500px] w-[500px] spot pointer-events-none" />

      <header className="relative px-5 md:px-8 py-5 flex items-center justify-between border-b border-white/10">
        <Link to="/" className="flex items-center gap-2.5" data-testid="admin-login-brand">
          <div className="h-9 w-9 rounded-xl bg-emerald text-white flex items-center justify-center shadow-emerald">
            <Zap className="h-4.5 w-4.5" strokeWidth={2.5} fill="currentColor" />
          </div>
          <div>
            <div className="font-display font-extrabold text-[18px] tracking-[-0.03em]">
              Swift<span className="text-emerald">Cash</span>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45 mt-0.5">Admin panel</div>
          </div>
        </Link>
        <Link to="/" className="text-[12px] font-bold text-white/55 hover:text-white inline-flex items-center gap-1.5" data-testid="admin-login-back">
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.4} /> Back to site
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 py-16 relative">
        <div className="w-full max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald/15 border border-emerald/30 text-emerald font-mono text-[11px] uppercase tracking-[0.12em] font-bold mb-6">
            <ShieldCheck className="h-3 w-3" strokeWidth={2.6} />
            Secure sign-in
          </div>
          <h1 className="font-display font-extrabold text-[48px] md:text-[64px] leading-[0.95] tracking-[-0.04em] mb-4">
            Control <span className="text-emerald">room</span>.
          </h1>
          <p className="text-white/60 mb-10 leading-relaxed max-w-sm font-medium">
            Review applications, update statuses, see the pipeline at a glance.
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <Label className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/55 mb-2 block">Email</Label>
              <Input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                placeholder="admin@swiftcapital.co.uk"
                className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12 rounded-xl font-semibold focus-visible:ring-emerald focus-visible:border-emerald"
                data-testid="admin-login-email"
              />
            </div>
            <div>
              <Label className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/55 mb-2 block">Password</Label>
              <Input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12 rounded-xl font-semibold focus-visible:ring-emerald focus-visible:border-emerald"
                data-testid="admin-login-password"
              />
            </div>
            <button
              type="submit" disabled={loading} data-testid="admin-login-submit"
              className="btn-shine group w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-emerald text-white font-extrabold hover:bg-emerald-2 transition-colors disabled:opacity-50 shadow-emerald"
            >
              {loading ? "Signing in…" : <>Sign in <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.6} /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
